import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { HOME_DASHBOARD_SLUG, renderUrl, type DashboardTarget } from '../../../lib/dashboard';
import { SECTION_PATH, NavSection, type NavDestination } from '../../../lib/pages';
import { useBasePath } from '../../../lib/use-base-path';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { callWidgetTool } from '../../../lib/widgets';
import { API_BASE } from '../../../lib/api-base';
import { useTranslation } from '../../../lib/i18n';
import { useConfirm } from '../../ConfirmDialog';
import { SYM_MARKER } from './constants';
import { useTheme } from '../../../lib/theme-context';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { widgetBiEvent } from '../../../lib/widget-bi';
import {
  DASHBOARD_WIDGET_ERROR_COMPONENT,
  dashboardEntity,
  dashboardPageFields,
  widgetErrorFields,
} from '../../../lib/dashboard-render-bi';
import { actPage, actRecord, actVersion } from '../../../lib/card-actions';
import { useCardAction } from './use-card-action';
import { useWorkspaces } from '../../../lib/workspaces';
import { spaceIdForSlug } from '../../../lib/conversation-space';
import { createSpaceIdGate } from '../../../lib/space-id-gate';
import { widgetChat, type ChatRiders } from '../../../lib/widget-chat';
import { readCalendarItemCard } from '../../../lib/calendar/calendar-item-card';
import { recordNames } from '../../../lib/owner-display-text';
import { encodeNavParams, navParamsRoute, parseNavParams } from '../../../lib/nav-params';
import { readWidgetVmSnapshot, writeWidgetVm } from '../../../lib/widget-vm-store';

type WidgetMessage = {
  __symWidget?: string;
  id?: string;
  kind?: string;
  tool?: string;
  params?: unknown;
  prompt?: string;
  context?: string;
  calendarItem?: unknown;
  names?: unknown;
  title?: string;
  message?: string;
  // The failing widget's slug, structured. It is ALSO prefixed into `message` for the banner, but a
  // telemetry row keyed off a display string stops matching reality the moment that copy changes.
  slug?: string;
  destination?: NavDestination;
  height?: number;
  // kind 'bi' (bridge-shim sym.bi): see lib/widget-bi.
  event?: string;
  widget?: string;
  action?: string;
  fields?: Record<string, unknown>;
  // kind 'act' (bridge-shim sym.act): a card's direct write, `widget` naming the card, `fallback` its chat prompt.
  // On a drill-in page a button outside every card slot sends `page` with an empty `widget`.
  page?: unknown;
  op?: string;
  record?: unknown;
  fallback?: unknown;
  fallbackCalendarItem?: unknown;
  fallbackNames?: unknown;
  version?: unknown;
  args?: unknown;
  rid?: unknown;
  // kind 'vmSet' / 'vmDrop' (bridge-shim __symVmStore): one widget's cached view-model.
  key?: unknown;
  value?: unknown;
};

export function useDashboardFrame({
  target,
  reloadKey,
  workspace,
  navParams: rawNavParams,
  docHeight,
  onHeight,
  onReload,
}: {
  target: DashboardTarget;
  reloadKey: number;
  workspace?: string;
  // The drill-in's params, as the route's raw `params` query value (lib/nav-params). The page reads them with
  // `sym.params()`; they never reach the render URL, so they do not split the render cache.
  navParams?: string;
  docHeight: number | null;
  onHeight: (height: number) => void;
  onReload?: () => void;
}) {
  const router = useRouter();
  const basePath = useBasePath();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { logItemSeen, logClickCta, logLoadFinish } = useGenericBi();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  // The `message` listener below is registered ONCE (its effect depends only on `addError`), so anything
  // it reads from render scope is frozen at first render. `target` changes as the reader moves between
  // dashboards and drill-in pages without remounting, so the failure report reads it through a ref —
  // otherwise every row after the first navigation would name the dashboard the reader STARTED on, and a
  // mis-attributed row is worse than a missing one because it looks like data.
  const targetRef = useRef(target);
  targetRef.current = target;
  const addError = useCallback((line: string) => setErrors((prev) => (prev.includes(line) ? prev : [...prev, line])), []);
  const [contentReady, setContentReady] = useState(false);

  const src = useMemo(() => renderUrl(target, reloadKey), [target.dashboard, target.page, reloadKey]);
  // Canonical form ('' for none): DashboardFrame keys the iframe on it, because a page reads its params once,
  // on load — a keep-alive surface moving from one booking to the next keeps the same `src`, and without a
  // fresh document it would go on showing the first booking.
  const paramsKey = encodeNavParams(parseNavParams(rawNavParams)) ?? '';
  const navParams = useMemo(() => parseNavParams(paramsKey), [paramsKey]);

  // The dashboard document is its own — the host's `data-theme` cannot reach it through the cascade,
  // so it is TOLD which of its two palettes to wear, exactly as the phones tell it
  // (`PageWebView.deliverTheme`).
  const { theme } = useTheme();
  const postTheme = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({ __symWidget: SYM_MARKER, kind: 'theme', theme }, '*');
  }, [theme]);

  // A theme sent before the document is ready is dropped, so it goes on load AND on every flip.
  // No reload — the runtime recolours in place.
  useEffect(() => {
    postTheme();
  }, [postTheme]);

  // The widgets' cached view-models are handed over only while the frame's own document is still loading (the bridge
  // asks from <head>). The frame can navigate itself, and the document that arrives then shares the same window, so
  // after the first load the answer is empty.
  const vmSnapshotOpenRef = useRef(true);
  const onIframeLoad = useCallback(() => {
    vmSnapshotOpenRef.current = false;
    postTheme();
    requestAnimationFrame(() => setContentReady(true));
  }, [postTheme]);

  // The Space this dashboard belongs to — none for the home dashboard or a global drill-in page. Its chats open
  // in that Space and its widgets' tool calls carry it, so the harness acts on that Space's site and data.
  const workspaces = useWorkspaces();
  const spaceId = spaceIdForSlug(workspace, workspaces.status === 'ready' ? workspaces.workspaces : []);
  // A widget call waits for the Space id: one sent before the Spaces list loads would go with no Space, and the
  // harness then cannot tell which business the card is for (lib/space-id-gate.ts).
  const spaceGateRef = useRef(createSpaceIdGate());
  spaceGateRef.current.update(!workspace || workspaces.status !== 'loading', spaceId);
  const openSideChat = useOpenSideChat();
  const openSideChatRef = useRef(openSideChat);
  openSideChatRef.current = openSideChat;
  const openChat = useCallback(
    (text: string, riders: ChatRiders = {}) =>
      openSideChatRef.current({ kind: 'new', message: text, workspaceId: spaceId, ...riders }),
    [spaceId],
  );
  const cardAction = useCardAction({
    dashboardSlug: target.dashboard || HOME_DASHBOARD_SLUG,
    onFallback: openChat,
    onWritten: () => onReload?.(),
  });

  const frameKey = `${src}\n${paramsKey}`;
  const prevSrcRef = useRef(frameKey);
  if (prevSrcRef.current !== frameKey) {
    prevSrcRef.current = frameKey;
    vmSnapshotOpenRef.current = true;
    setContentReady(false);
  }

  // The frame's own document path, which scopes the widget view-models it may read and write.
  const documentPath = src.split('?')[0];
  const navRef = useRef({ router, basePath, workspace, spaceId, onHeight, t, confirm, logItemSeen, logClickCta, logLoadFinish, openChat, cardAction, navParams, documentPath });
  navRef.current = { router, basePath, workspace, spaceId, onHeight, t, confirm, logItemSeen, logClickCta, logLoadFinish, openChat, cardAction, navParams, documentPath };
  const onMessage = (e: MessageEvent) => {
    const iframe = iframeRef.current;
    if (!iframe || e.source !== iframe.contentWindow) return;
    const d = e.data as WidgetMessage;
    if (!d || d.__symWidget !== SYM_MARKER) return;
    const reply = (payload: object) => iframe.contentWindow?.postMessage({ __symWidget: SYM_MARKER, ...payload }, '*');
    const {
      router: r,
      basePath: bp,
      workspace: ws,
      spaceId: space,
      onHeight: reportHeight,
      t: translate,
      confirm: ask,
      openChat: chat,
      cardAction: card,
      navParams: pageParams,
    } = navRef.current;

    if (d.kind === 'call' && d.id) {
      spaceGateRef.current
        .get()
        .then((known) => callWidgetTool(d.tool || '', d.params, known ?? space, ws))
        .then((result) => reply({ id: d.id, ok: true, result }))
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : String(err);
          reply({ id: d.id, ok: false, error: message });
          // A failed tool call is not a page error — a widget probing data absent for this account fails
          // routinely and then shows its own empty/error state. The banner is raised only when a widget
          // actually fails to render, via the runtime's `kind: 'error'` message (below). Reply, don't banner.
        });
    } else if (d.kind === 'vmSnapshot' && d.id) {
      reply({ id: d.id, ok: true, result: vmSnapshotOpenRef.current ? readWidgetVmSnapshot(navRef.current.documentPath) : {} });
    } else if (d.kind === 'vmSet' && typeof d.key === 'string' && typeof d.value === 'string') {
      writeWidgetVm(navRef.current.documentPath, d.key, d.value);
    } else if (d.kind === 'vmDrop' && typeof d.key === 'string') {
      writeWidgetVm(navRef.current.documentPath, d.key, null);
    } else if (d.kind === 'params' && d.id) {
      // `sym.params()`: always answered — `{}` when this page was opened without params — because the shim waits
      // 400 ms for a reply before giving up, and every page load would pay that wait on a host that stays quiet.
      reply({ id: d.id, ok: true, result: pageParams });
    } else if (d.kind === 'confirm' && d.id) {
      // Same shape as the phones' native alert: the widget's title (or "Are you sure?"), its message, Confirm.
      void card
        .guardWidgetConfirm((signal) =>
          ask({
            title: d.title || translate('common.areYouSure'),
            message: d.message,
            confirmLabel: translate('common.confirm'),
            bi: { page_name: 'dashboard', modal_name: 'widget_confirm' },
            signal,
          }),
        )
        .then((ok) => reply({ id: d.id, ok: true, result: ok }));
    } else if (d.kind === 'act' && typeof d.widget === 'string' && typeof d.op === 'string') {
      card.act(
        {
          widget: d.widget,
          page: actPage(d.page),
          op: d.op,
          record: actRecord(d.record),
          version: actVersion(d.version),
          fallback: typeof d.fallback === 'string' ? d.fallback : '',
          fallbackCalendarItem: readCalendarItemCard(d.fallbackCalendarItem),
          fallbackNames: recordNames(d.fallbackNames),
          args: d.args,
          rid: typeof d.rid === 'string' ? d.rid : undefined,
        },
        reply,
      );
    } else if (d.kind === 'chat') {
      const { message, ...riders } = widgetChat(d);
      chat(message, riders);
    } else if (d.kind === 'navigate' && d.destination) {
      const destination = d.destination;
      if ('workspace' in destination) {
        void r.push(
          { pathname: '/workspace/[slug]', query: { slug: destination.workspace } },
          `${bp}/workspace/${encodeURIComponent(destination.workspace)}`,
        );
      } else if ('page' in destination) {
        // A card's `nav: { page, params }` rides the route as one `?params=` key (empty when there are none,
        // so a plain drill-in's URL is unchanged); the opened page answers `sym.params()` from it.
        const extra = navParamsRoute(destination.params);
        if (ws) {
          void r.push(
            { pathname: '/workspace/[slug]/pages/[page]', query: { slug: ws, page: destination.page, ...extra.query } },
            `${bp}/workspace/${encodeURIComponent(ws)}/pages/${encodeURIComponent(destination.page)}${extra.search}`,
          );
        } else {
          void r.push(
            { pathname: '/pages/[slug]', query: { slug: destination.page, ...extra.query } },
            `${bp}/pages/${encodeURIComponent(destination.page)}${extra.search}`,
          );
        }
      } else {
        // A destination shape this host does not know must NOT move the user. `SECTION_PATH[undefined]`
        // fell through to Home, so a library widget emitting `{app: …}` (appointment-detail's "Go to
        // Booking Calendar" does) threw the reader out of the page they were reading, silently. Losing
        // your place is worse than a tap that does nothing, and a dead tap is at least diagnosable.
        const section = (destination as { section?: string }).section;
        if (section && section in SECTION_PATH) {
          void r.push(`${bp}${SECTION_PATH[section as NavSection]}`); // a built-in section is another screen
        } else {
          console.warn('[dashboard-nav] ignored a destination this host does not handle', destination);
        }
      }
    } else if (d.kind === 'error') {
      // Raised only by the runtime binder when a widget genuinely fails to render (tagged with its slug);
      // the runtime no longer forwards raw window errors, so this is a real, actionable widget failure.
      // Logged as well as collected: with the banner classifying, a page whose only failures are a missing connection or a rate limit offers no
      // "Copy details", so the log is where the raw diagnostic stays readable.
      console.warn('dashboard: widget runtime error', d.message);
      // AND server-side. This surface renders every dashboard on web (a workspace's dashboard and every
      // drill-in page), so the metric's denominator matches both phones'. Fire-and-forget —
      // `API_BASE` for the active mount, plain `fetch` so an unauthenticated instance cannot redirect a
      // reader away from the page they are looking at, `keepalive` so it survives their leaving.
      void fetch(`${API_BASE}/api/widget-runtime-error`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          widgetSlug: d.slug ?? '',
          message: d.message ?? '',
          dashboardSlug: targetRef.current.page || targetRef.current.dashboard || '',
        }),
        keepalive: true,
      }).catch(() => undefined);
      // …and counted in BI (src:198 loadFinish, isSuccessful=false), so a monitor that reads only the BI
      // tables can count failed widgets against dashboard renders — the same event both phones fire. Ids and
      // the error CLASS only; the message is classified, never sent (lib/dashboard-render-bi).
      const errBase = dashboardPageFields(targetRef.current.dashboard, targetRef.current.page);
      navRef.current.logLoadFinish(
        DASHBOARD_WIDGET_ERROR_COMPONENT,
        { isSuccessful: false },
        { entity: dashboardEntity(errBase), additionalFields: widgetErrorFields(errBase, d.slug, d.message) },
      );
      addError(d.message || translate('dashboard.pageError'));
    } else if (d.kind === 'errorCleared') {
      setErrors([]);
    } else if (d.kind === 'height' && typeof d.height === 'number') {
      reportHeight(d.height);
    } else if (d.kind === 'bi') {
      // A widget's impression or action press, sent by the dashboard runtime (198:6 / 198:10).
      // A drill-in page renders through this frame too (`target.page`), so it is a page surface, not home/space.
      const page = targetRef.current.page;
      const ev = widgetBiEvent(
        d,
        page ? { surface: 'page', page, space: ws } : ws ? { surface: 'space', space: ws } : { surface: 'home' },
      );
      if (ev) {
        const { logItemSeen: seen, logClickCta: click } = navRef.current;
        (ev.type === 'itemSeen' ? seen : click)(ev.componentName, { additionalFields: ev.additionalFields });
      }
    }
  };
  useEffect(() => {
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [addError]);

  useEffect(() => {
    if (docHeight == null) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    const scroller = iframe.closest('.orion-content-pane') as HTMLElement | null;
    let raf = 0;
    const push = () => {
      raf = 0;
      const ifr = iframeRef.current;
      if (!ifr || !ifr.contentWindow) return;
      const ir = ifr.getBoundingClientRect();
      const sr = (scroller ?? document.documentElement).getBoundingClientRect();
      const top = Math.max(0, sr.top - ir.top);
      const h = Math.max(0, Math.min(ir.height, sr.bottom - ir.top) - top);
      ifr.contentWindow.postMessage({ __symWidget: SYM_MARKER, kind: 'viewport', top, h }, '*');
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(push);
    };
    scroller?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    push();
    return () => {
      scroller?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [src, docHeight]);

  useEffect(() => setErrors([]), [src, paramsKey]);

  return { iframeRef, src, paramsKey, basePath, errors, contentReady, onIframeLoad, cardActionElement: cardAction.element };
}
